import { useEffect, useState } from "react";
import { Check, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";
import { HostingExplorer } from "@/components/stage/HostingExplorer";

/** Stage Hostings: the first is free forever (everything inside it is free); each extra Hosting is $20 / 30 days. */
export function StageRoles() {
  const [st, setSt] = useState(null);
  const [buying, setBuying] = useState(null); // hosting key being purchased
  const load = () => api.get("/live/roles").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;

  const entry = (key) => st.roles.find((r) => r.key === key);
  const pick = async (key) => {
    const e = entry(key);
    if (e?.active) {
      try { const { data } = await api.delete(`/live/roles/${key}`); setSt(data); setBuying(null); toast.success("Hosting removed"); }
      catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Could not remove"); }
      return;
    }
    if (st.free_available) {
      try { const { data } = await api.post("/live/roles", { role: key }); setSt(data); toast.success("Your free Hosting is set — everything inside it is free"); }
      catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Could not add"); }
      return;
    }
    setBuying(key);
  };
  const buyingLabel = st.catalog.find((r) => r.key === buying)?.label;

  return (
    <div className="rounded-3xl border border-white/10 bg-[#0F0F10] p-5" data-testid="stage-roles">
      <div className="text-xs uppercase tracking-widest text-neutral-500">Your Hostings</div>
      <div className="mt-1 text-sm text-neutral-400">
        {st.admin ? <>As <span className="text-white font-semibold">admin</span> you hold every Hosting — all 8 Stages and every format inside them are open to you, with no plan or add-on needed.</> : <>
        A Hosting is the kind of Stage you run. <span className="text-white font-semibold">Your first Hosting is free</span> — every role, format, player and performer inside it is free, and so is your first live session.
        {" "}Each <span className="text-white font-semibold">additional Hosting is ${st.addon_price_usd}/month</span>, alongside the VISMARKS plan once your free period ends.</>}
      </div>
      <div className="mt-4">
        <HostingExplorer title="Your Stages" onPick={st.admin ? undefined : pick} compact
          status={(key) => { const e = entry(key); const on = !!e?.active; return { on, free: !!e?.free, label: on ? "Active" : st.free_available ? "Claim as your free Hosting" : `Add for $${st.addon_price_usd}/month` }; }} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="roles-summary">
        {st.catalog.map((r) => {
          const e = entry(r.key); const on = !!e?.active;
          return (
            <span key={r.key} data-testid={`role-status-${r.key}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] ${on ? "border-[#34C759]/50 text-[#34C759]" : "border-white/10 text-neutral-500"}`}>
              {on ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}{r.label.replace(" Host", "")}{on && e.free ? " · free" : on ? ` · until ${new Date(e.until).toLocaleDateString()}` : ""}
            </span>
          );
        })}
      </div>
      {buying && (
        <div className="mt-4 rounded-2xl border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-4" data-testid="roles-addon-checkout">
          <div className="font-bold text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#FF9F0A]" />Add {buyingLabel} · ${st.addon_price_usd} for 30 days</div>
          <div className="text-xs text-neutral-400 mt-0.5 mb-3">Everything inside this Hosting is included. Renews only when you pay again.</div>
          <PaymentMethodPicker purpose="roles_addon" amountUsd={st.addon_price_usd} extraBody={{ role: buying }} providers={{ paystack: true, flutterwave: true, stripe: false }} />
          <button type="button" onClick={() => setBuying(null)} className="mt-2 text-[11px] text-neutral-500 hover:text-white underline" data-testid="roles-addon-cancel">Not now</button>
        </div>
      )}
    </div>
  );
}
